import { useEffect, useMemo, useState } from "react";
import Shell from "../components/Shell";
import {
  fetchGroups,
  fetchCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  campaignSendNow,
  fetchStarterTemplates,
  regenerateCampaignImages,
  regenerateCampaignTemplateImage,
  fetchSettings,
  updateSettings,
  sendCampaignReportNow,
} from "../lib/api";
import {
  CalendarCheckIcon,
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  TrashIcon,
  DownloadSimpleIcon,
  CaretDownIcon,
  ImageIcon,
  ArrowsClockwiseIcon,
  RobotIcon,
  EnvelopeSimpleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function defaultStart() {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60 * 1000).toISOString().slice(0, 16);
}

export default function CampaignsPage() {
  const [groups, setGroups] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("Europacific Drip");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [interval, setIntervalDays] = useState(14);
  const [startAt, setStartAt] = useState(defaultStart());
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);

  const loadGroups = async () => setGroups(await fetchGroups());
  const loadCampaigns = async () => setCampaigns(await fetchCampaigns());
  const loadSettings = async () => setSettings(await fetchSettings());

  useEffect(() => {
    loadGroups();
    loadCampaigns();
    loadSettings();
  }, []);

  const onToggleReport = async () => {
    const next = !settings?.campaign_report_enabled;
    const merged = { ...settings, campaign_report_enabled: next };
    setSettings(merged);
    try {
      await updateSettings(merged);
      toast.success(next ? "Weekly report ON — emails every Monday" : "Weekly report OFF");
    } catch {
      setSettings((s) => ({ ...s, campaign_report_enabled: !next }));
      toast.error("Could not update");
    }
  };

  const onSendReport = async () => {
    setSendingReport(true);
    try {
      const r = await sendCampaignReportNow();
      if (r.sent) toast.success(`Report emailed to ${r.to}`);
      else if (r.reason === "email_not_configured")
        toast.error("Email isn't configured yet (works once deployed to production)");
      else if (r.error) toast.error(`Send failed: ${String(r.error).slice(0, 80)}`);
      else toast.message("Report built but not sent", { description: r.reason || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send report");
    } finally {
      setSendingReport(false);
    }
  };

  // Poll while any campaign is still generating its template images.
  const anyGenerating = useMemo(
    () => campaigns.some((c) => (c.templates || []).some((t) => t.image_status === "pending")),
    [campaigns]
  );
  useEffect(() => {
    if (!anyGenerating) return;
    const t = setInterval(loadCampaigns, 6000);
    return () => clearInterval(t);
  }, [anyGenerating]);

  const onRegenAll = async (c) => {
    try {
      await regenerateCampaignImages(c.id);
      toast.success("Regenerating all images…");
      setCampaigns((prev) =>
        prev.map((x) =>
          x.id === c.id
            ? { ...x, templates: (x.templates || []).map((t) => ({ ...t, image_status: "pending", image_base64: null })) }
            : x
        )
      );
    } catch {
      toast.error("Could not regenerate");
    }
  };

  const onRegenOne = async (c, idx) => {
    try {
      await regenerateCampaignTemplateImage(c.id, idx);
      toast.success("Regenerating image…");
      setCampaigns((prev) =>
        prev.map((x) =>
          x.id === c.id
            ? { ...x, templates: (x.templates || []).map((t, i) => (i === idx ? { ...t, image_status: "pending", image_base64: null } : t)) }
            : x
        )
      );
    } catch {
      toast.error("Could not regenerate");
    }
  };

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => !q || (g.group_name || "").toLowerCase().includes(q));
  }, [groups, query]);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const loadStarter = async () => {
    try {
      const { templates: t } = await fetchStarterTemplates();
      setTemplates(t);
      // auto-select Panco + Seajet if present
      const auto = new Set(selected);
      groups.forEach((g) => {
        const nm = (g.group_name || "").toLowerCase();
        if (nm.includes("panco") || nm.includes("seajet")) auto.add(g.group_id);
      });
      setSelected(auto);
      toast.success(`Loaded ${t.length} Europacific templates`);
    } catch {
      toast.error("Could not load starter templates");
    }
  };

  const onCreate = async () => {
    if (templates.length === 0) return toast.error("Load or add templates first");
    if (selected.size === 0) return toast.error("Select at least one group");
    const ids = [...selected];
    const names = ids.map((id) => groups.find((g) => g.group_id === id)?.group_name || id);
    setSaving(true);
    try {
      await createCampaign({
        name: name.trim() || "Campaign",
        group_ids: ids,
        group_names: names,
        interval_days: Number(interval) || 14,
        templates: templates.map((t) => ({ title: t.title, text: t.text })),
        start_at: new Date(startAt).toISOString(),
        enabled: true,
      });
      toast.success("Campaign created — generating images in the background…");
      setTemplates([]);
      setSelected(new Set());
      loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create campaign");
    } finally {
      setSaving(false);
    }
  };

  const onToggleEnabled = async (c) => {
    setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, enabled: !x.enabled } : x)));
    try {
      await updateCampaign(c.id, { enabled: !c.enabled });
    } catch {
      loadCampaigns();
      toast.error("Update failed");
    }
  };

  const onSendNow = async (c) => {
    if (!window.confirm(`Send the next template ("${(c.templates[c.next_index] || {}).title || ""}") to ${c.group_ids.length} group(s) now?`)) return;
    try {
      await campaignSendNow(c.id);
      toast.success("Queued — sends in ~2 min via the listener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await deleteCampaign(c.id);
      setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const allVisibleSelected = visibleGroups.length > 0 && visibleGroups.every((g) => selected.has(g.group_id));

  return (
    <Shell title="Campaigns" subtitle="Rotate a set of message templates to your groups on a fixed schedule.">
      <div className="max-w-5xl space-y-6" data-testid="campaigns-page">
        {/* Automation + weekly report */}
        <div className="flex flex-col gap-3 border border-zinc-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="campaign-automation-card">
          <div className="flex items-start gap-3">
            <RobotIcon size={22} weight="duotone" className="mt-0.5 shrink-0 text-zinc-800" />
            <div>
              <div className="text-sm font-semibold text-zinc-900">Fully automated</div>
              <p className="text-xs text-zinc-500">
                Active campaigns post the next template to your groups on their own — no clicks needed.
                You just get a <span className="font-medium text-zinc-700">weekly email report</span> every Monday.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              data-testid="campaign-report-test-btn"
              onClick={onSendReport}
              disabled={sendingReport}
              className="flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              <EnvelopeSimpleIcon size={14} /> {sendingReport ? "Sending…" : "Send test report"}
            </button>
            <label className="flex items-center gap-2 text-xs text-zinc-700" data-testid="campaign-report-toggle">
              <span>Weekly report</span>
              <button
                onClick={onToggleReport}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center transition-colors ${settings?.campaign_report_enabled ? "bg-zinc-950" : "bg-zinc-300"}`}
              >
                <span className={`inline-block h-4 w-4 bg-white transition-transform ${settings?.campaign_report_enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </label>
          </div>
        </div>

        {/* Create */}
        <div className="border border-zinc-200 bg-white p-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Campaign name</label>
                <input data-testid="campaign-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900" />
              </div>
              <button
                data-testid="campaign-load-starter"
                onClick={loadStarter}
                className="flex items-center gap-1.5 border border-zinc-900 bg-zinc-900 px-3 py-1.5 text-sm text-white hover:bg-zinc-700"
              >
                <DownloadSimpleIcon size={16} /> Load Europacific templates (12)
              </button>
              <div className="text-xs text-zinc-500" data-testid="campaign-template-count">
                {templates.length > 0 ? (
                  <>
                    <span className="font-medium text-zinc-800">{templates.length} templates loaded.</span> Sent one per cycle, in order. A branded image (with your europacific logo) is auto-generated for each.
                    <div className="mt-2 max-h-40 overflow-y-auto border border-zinc-100">
                      {templates.map((t, i) => (
                        <div key={i} className="border-b border-zinc-100 px-2 py-1.5 last:border-b-0">
                          <div className="font-medium text-zinc-700">{i + 1}. {t.title}</div>
                          <div className="truncate text-zinc-400">{t.text.replace(/\n/g, " ")}</div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  "No templates yet — click the button above to load the starter set."
                )}
              </div>
              <p className="text-xs text-zinc-500">
                💡 <code className="bg-zinc-100 px-1">{"{{First Name}}"}</code> is replaced with each group's name automatically.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Target groups · <span className="text-zinc-900">{selected.size} selected</span>
                </label>
                <button
                  data-testid="campaign-select-all"
                  onClick={() =>
                    setSelected((prev) => {
                      const n = new Set(prev);
                      const all = visibleGroups.every((g) => n.has(g.group_id));
                      visibleGroups.forEach((g) => (all ? n.delete(g.group_id) : n.add(g.group_id)));
                      return n;
                    })
                  }
                  className="text-xs text-zinc-600 underline"
                >
                  {allVisibleSelected ? "Clear visible" : "Select visible"}
                </button>
              </div>
              <div className="flex items-center gap-2 border border-zinc-300 px-2">
                <MagnifyingGlassIcon size={16} className="text-zinc-400" />
                <input data-testid="campaign-group-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search groups" className="w-full py-1.5 text-sm outline-none" />
              </div>
              <div className="max-h-40 overflow-y-auto border border-zinc-200" data-testid="campaign-group-list">
                {visibleGroups.map((g) => (
                  <label key={g.group_id} data-testid={`campaign-group-${g.group_id}`} className="flex cursor-pointer items-center gap-2 border-b border-zinc-100 px-3 py-2 text-sm hover:bg-zinc-50">
                    <input type="checkbox" checked={selected.has(g.group_id)} onChange={() => toggle(g.group_id)} className="accent-zinc-900" />
                    <span className="flex-1 truncate">{g.group_name || g.group_id}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Every (days)</label>
                  <input data-testid="campaign-interval" type="number" min={1} value={interval} onChange={(e) => setIntervalDays(e.target.value)} className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">First send</label>
                  <input data-testid="campaign-start" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900" />
                </div>
              </div>
              <button
                data-testid="campaign-create-btn"
                onClick={onCreate}
                disabled={saving}
                className="w-full bg-zinc-900 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50"
              >
                {saving ? "Creating…" : "Create campaign"}
              </button>
            </div>
          </div>
        </div>

        {/* Existing campaigns */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">Active campaigns</h2>
          {campaigns.length === 0 && <p className="text-sm text-zinc-400">No campaigns yet.</p>}
          <div className="space-y-2" data-testid="campaign-list">
            {campaigns.map((c) => {
              const nextTpl = (c.templates || [])[c.next_index] || {};
              return (
                <div key={c.id} className="border border-zinc-200 bg-white" data-testid={`campaign-item-${c.id}`}>
                  <div className="flex items-center gap-3 p-3">
                    <button
                      data-testid={`campaign-toggle-${c.id}`}
                      onClick={() => onToggleEnabled(c)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center transition-colors ${c.enabled ? "bg-zinc-950" : "bg-zinc-300"}`}
                      title={c.enabled ? "Active" : "Paused"}
                    >
                      <span className={`inline-block h-4 w-4 bg-white transition-transform ${c.enabled ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-zinc-900">{c.name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                        <span>every {c.interval_days}d</span>
                        <span>·</span>
                        <span>{(c.group_ids || []).length} group(s)</span>
                        <span>·</span>
                        <span>{(c.templates || []).length} templates</span>
                        <span>·</span>
                        <span data-testid={`campaign-images-ready-${c.id}`}>
                          {(c.templates || []).filter((t) => t.image_base64).length}/{(c.templates || []).length} images
                        </span>
                        <span>·</span>
                        <span>next: <span className="text-zinc-700">{nextTpl.title || "—"}</span></span>
                        <span>·</span>
                        <span>{c.next_run_at ? new Date(c.next_run_at).toLocaleString() : ""}</span>
                      </div>
                    </div>
                    <button data-testid={`campaign-sendnow-${c.id}`} onClick={() => onSendNow(c)} className="flex shrink-0 items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:bg-zinc-50">
                      <PaperPlaneTiltIcon size={14} /> Send next now
                    </button>
                    <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="p-1.5 text-zinc-400 hover:text-zinc-900">
                      <CaretDownIcon size={16} className={expanded === c.id ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>
                    <button data-testid={`campaign-delete-${c.id}`} onClick={() => onDelete(c)} className="p-1.5 text-zinc-400 hover:text-red-600">
                      <TrashIcon size={16} />
                    </button>
                  </div>
                  {expanded === c.id && (
                    <div className="border-t border-zinc-100 px-4 py-3">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Templates & images</span>
                        <button
                          data-testid={`campaign-regen-all-${c.id}`}
                          onClick={() => onRegenAll(c)}
                          className="flex items-center gap-1.5 border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
                        >
                          <ArrowsClockwiseIcon size={13} /> Regenerate all images
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {(c.templates || []).map((t, i) => (
                          <div
                            key={i}
                            data-testid={`campaign-template-card-${c.id}-${i}`}
                            className={`flex gap-3 border p-2 ${i === c.next_index ? "border-emerald-400 bg-emerald-50/40" : "border-zinc-100"}`}
                          >
                            <div className="relative h-20 w-20 shrink-0 overflow-hidden border border-zinc-200 bg-zinc-50">
                              {t.image_base64 ? (
                                <img
                                  data-testid={`campaign-template-image-${c.id}-${i}`}
                                  src={`data:image/jpeg;base64,${t.image_base64}`}
                                  alt={t.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-zinc-300">
                                  <ImageIcon size={20} />
                                  {t.image_status === "pending" && (
                                    <span className="text-[9px] animate-pulse text-zinc-400">generating…</span>
                                  )}
                                  {t.image_status === "failed" && (
                                    <span className="text-[9px] text-red-400">failed</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium text-zinc-800">
                                {i + 1}. {t.title} {i === c.next_index && <span className="text-emerald-600">← next</span>}
                              </div>
                              <div className="mt-0.5 line-clamp-2 text-[11px] text-zinc-400">{(t.text || "").replace(/\n/g, " ")}</div>
                              <button
                                data-testid={`campaign-regen-one-${c.id}-${i}`}
                                onClick={() => onRegenOne(c, i)}
                                className="mt-1.5 flex items-center gap-1 text-[11px] text-zinc-500 underline hover:text-zinc-900"
                              >
                                <ArrowsClockwiseIcon size={11} /> Regenerate
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-zinc-400">Sent so far: {(c.runs || []).length}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Shell>
  );
}
